import { useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../App';
import { usePermissions } from '@/hooks/usePermissions';
import { cn } from '@/lib/utils';
import { useNetwork } from '@/contexts/NetworkContext';
import { PwaInstallButton } from './PwaInstallButton';
import {
  Sheet, SheetContent, SheetTrigger
} from '@/components/ui/Sheet';
import {
  Avatar, AvatarFallback
} from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import {
  LayoutDashboard, Clock, FileText, FolderKanban, BarChart3, Wallet, Users, ShieldCheck, FileCode2, CalendarDays, Menu, LogOut, Globe, Wifi, WifiOff, CloudUpload
} from 'lucide-react';

const iconMap: Record<string, React.ReactNode> = {
  dashboard: <LayoutDashboard className="h-5 w-5 shrink-0" />,
  timeTracking: <Clock className="h-5 w-5 shrink-0" />,
  timesheets: <FileText className="h-5 w-5 shrink-0" />,
  planning: <CalendarDays className="h-5 w-5 shrink-0" />,
  projects: <FolderKanban className="h-5 w-5 shrink-0" />,
  reports: <BarChart3 className="h-5 w-5 shrink-0" />,
  budgets: <Wallet className="h-5 w-5 shrink-0" />,
  clients: <Users className="h-5 w-5 shrink-0" />,
  teams: <Users className="h-5 w-5 shrink-0" />,
  templates: <FileCode2 className="h-5 w-5 shrink-0" />,
  admin: <ShieldCheck className="h-5 w-5 shrink-0" />,
};

const OFFLINE_ALLOWED = new Set(['/', '/time-entries']);

interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  pageKey: string;
}

function SidebarLink({ item, disabled }: { item: NavItem; disabled?: boolean }) {
  const { canRead } = usePermissions({ pageKey: item.pageKey });
  const location = useLocation();
  if (!canRead) return null;
  const active = location.pathname === item.to || (item.to !== '/' && location.pathname.startsWith(item.to));

  if (disabled) {
    return (
      <span
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-[var(--color-slate-600)] cursor-not-allowed"
        title="This page requires an internet connection"
      >
        <span className="text-[var(--color-slate-600)]">{item.icon}</span>
        {item.label}
      </span>
    );
  }

  return (
    <Link
      to={item.to}
      onClick={() => {
        if ('ontouchstart' in window) {
          const sheet = document.querySelector('[data-state="open"]') as HTMLElement | null;
          if (sheet) sheet.click?.();
        }
      }}
      className={cn(
        'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors',
        active
          ? 'bg-[var(--color-primary)]/10 text-white'
          : 'text-[var(--color-slate-400)] hover:bg-[var(--color-slate-800)] hover:text-white'
      )}
    >
      <span className={cn('transition-colors', active ? 'text-[var(--color-primary)]' : 'text-[var(--color-slate-400)]')}>
        {item.icon}
      </span>
      {item.label}
      {active && (
        <span className="ml-auto h-2 w-2 rounded-full bg-[var(--color-primary)]" />
      )}
    </Link>
  );
}

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useContext(AuthContext);
  const { t, i18n } = useTranslation();
  const { isOnline, pendingCount, triggerSync } = useNetwork();

  const handleLogout = async () => {
    await logout();
  };

  const changeLanguage = (lng: string) => {
    i18n.changeLanguage(lng);
    window.location.reload();
  };

  const navItems: NavItem[] = [
    { to: '/', label: t('dashboard'), icon: iconMap.dashboard, pageKey: 'dashboard' },
    { to: '/time-entries', label: t('timeTracking'), icon: iconMap.timeTracking, pageKey: 'time-entries' },
    { to: '/timesheets', label: t('timesheets') || 'Timesheets', icon: iconMap.timesheets, pageKey: 'timesheets' },
    { to: '/planning', label: t('planning') || 'Planning', icon: iconMap.planning, pageKey: 'planned-hours' },
    { to: '/projects', label: t('projects'), icon: iconMap.projects, pageKey: 'projects' },
    { to: '/reports', label: t('reports'), icon: iconMap.reports, pageKey: 'reports' },
    { to: '/budgets', label: t('budgets') || 'Budgets', icon: iconMap.budgets, pageKey: 'budgets' },
    { to: '/clients', label: t('clients'), icon: iconMap.clients, pageKey: 'clients' },
    { to: '/teams', label: t('teams'), icon: iconMap.teams, pageKey: 'teams' },
    { to: '/templates', label: t('templates') || 'Templates', icon: iconMap.templates, pageKey: 'templates' },
    { to: '/admin', label: t('admin'), icon: iconMap.admin, pageKey: 'admin' },
  ];

  const SidebarContent = () => (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center gap-3 px-6">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-primary)] text-white font-bold text-sm">J</div>
        <span className="text-lg font-bold text-white tracking-tight">Jani</span>
      </div>

      <div className="px-6 pb-2">
        {isOnline ? (
          <div className="flex items-center gap-2 text-xs font-medium text-emerald-400">
            <Wifi className="h-3.5 w-3.5" /> Online
            {pendingCount > 0 && (
              <button onClick={triggerSync} className="ml-auto flex items-center gap-1 rounded-md bg-amber-500/20 px-2 py-0.5 text-amber-300 hover:bg-amber-500/30 transition-colors">
                <CloudUpload className="h-3 w-3" /> {pendingCount} pending
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2 text-xs font-medium text-amber-400" data-testid="offline-badge">
              <WifiOff className="h-3.5 w-3.5" /> Offline
            </div>
            {pendingCount > 0 && (
              <div className="flex items-center gap-1 text-xs text-amber-300/80">
                <CloudUpload className="h-3 w-3" /> {pendingCount} {pendingCount === 1 ? 'entry' : 'entries'} will sync on reconnect
              </div>
            )}
          </div>
        )}
      </div>

      <div className="px-3 pb-2">
        <PwaInstallButton />
      </div>

      <nav className="flex-1 space-y-1 px-3 py-4">
        {navItems.map((item) => (
          <SidebarLink key={item.to} item={item} disabled={!isOnline && !OFFLINE_ALLOWED.has(item.to)} />
        ))}
      </nav>

      <div className="border-t border-[var(--color-slate-800)] p-4">
        <div className="mb-3 flex items-center gap-3">
          <Avatar className="h-8 w-8 bg-[var(--color-primary)] text-white">
            <AvatarFallback className="bg-[var(--color-primary)] text-white text-xs">{user?.name?.charAt(0)?.toUpperCase() || 'U'}</AvatarFallback>
          </Avatar>
          <div className="flex flex-col">
            <span className="text-sm font-medium text-white truncate max-w-[140px]">{user?.name}</span>
            <span className="text-xs text-[var(--color-slate-500)]">{user?.email}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center rounded-lg border border-[var(--color-slate-700)] bg-[var(--color-slate-900)] px-2 py-1.5">
            <Globe className="mr-1.5 h-3.5 w-3.5 text-[var(--color-slate-400)]" />
            <select
              value={i18n.language}
              onChange={(e) => changeLanguage(e.target.value)}
              className="bg-transparent text-xs text-white outline-none cursor-pointer"
            >
              <option value="en" className="bg-[var(--color-slate-900)]">EN</option>
              <option value="fr" className="bg-[var(--color-slate-900)]">FR</option>
              <option value="de" className="bg-[var(--color-slate-900)]">DE</option>
            </select>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} className="flex-1 justify-start text-[var(--color-slate-400)] hover:text-white hover:bg-[var(--color-slate-800)]">
            <LogOut className="mr-2 h-4 w-4" />
            <span className="text-xs">{t('logout')}</span>
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-dvh bg-[var(--color-background)]">
      {/* Desktop sidebar */}
      <aside className="sticky top-0 hidden h-dvh w-60 shrink-0 flex-col border-r border-[var(--color-border)] bg-[var(--color-slate-950)] lg:flex overflow-y-auto">
        <SidebarContent />
      </aside>

      {/* Main wrapper: top bar + content stacked vertically */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Mobile top bar */}
        <div className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-[var(--color-border)] bg-[var(--color-card)] px-4 shadow-sm lg:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9">
                <Menu className="h-5 w-5" />
                <span className="sr-only">Open menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-60 border-r border-[var(--color-border)] bg-[var(--color-slate-950)] p-0 text-white">
              <SidebarContent />
            </SheetContent>
          </Sheet>
          <span className="text-lg font-bold tracking-tight">Jani</span>
        </div>

        {/* Mobile PWA install banner */}
        <div className="lg:hidden">
          <PwaInstallButton />
        </div>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="mx-auto max-w-7xl p-3 sm:p-5 lg:p-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
